import React, { useState, useEffect, useRef } from 'react'
import { makeStyles, Popover, Menu } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  useDialogs,
  Editor,
  NoteHistoryPopover,
  MenuItem,
  useClientOptions
} from '@paintscout/ui'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import type { Company, Contact, Deal, NoteActivity } from '@paintscout/api'
import { useCompanyQuery, useContactQuery, useDealQuery, useGetNoteQuery } from '@paintscout/api'
import type { QuoteFile } from 'paintscout'
import { EditItemFilesDialog, EditIntegrationItemFilesDialog } from '@paintscout/react-quote'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import MediaIcon from '@material-ui/icons/PermMedia'
import striptags from 'striptags'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { hasIntegrationInterface } from '@paintscout/util/builder'
const useStyles = makeStyles<Theme, NoteDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {},
    cancel: {},
    confirm: {},
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(1.5)
    },
    delete: {
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.error.main
      },
      '&:active': {
        color: theme.palette.error.main
      }
    }
  }),
  { name: 'NoteDialog' }
)

export interface NoteDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  noteId?: string
  docId: string
  onConfirm: (noteDoc: Contact | Company | Deal, note: string, files: QuoteFile[], isDirty?: boolean) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
  onDelete?: (note: NoteActivity) => any
}

function NoteDialog(props: NoteDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, onDelete, docId, noteId, ...baseDialogProps } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const { crm: crmFlag } = useFlags()
  const { options } = useClientOptions()
  const hasCompanyCam = hasIntegrationInterface({ options, integration: 'companycam' })
  const notesRef = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const isCreating = !noteId
  // change this to check for permissions
  const deleteEnabled = true
  const {
    data,
    error,
    loading: fetching
  } = useGetNoteQuery({
    variables: { id: noteId },
    skip: !noteId,
    fetchPolicy: 'cache-and-network'
  })
  const [note, setNote] = useState<NoteActivity>(data?.getNote)
  const [noteDoc, setNoteDoc] = useState(null)
  const {
    data: contactData,
    loading: isLoadingContact,
    error: _contactError
  } = useContactQuery({
    variables: { id: note?.details?.contact?.id },
    skip: isCreating || note?.details?.docType !== 'contact',
    fetchPolicy: 'cache-and-network'
  })

  const {
    data: companyData,
    loading: isLoadingCompany,
    error: _companyError
  } = useCompanyQuery({
    variables: { id: docId }, // uses docId as it can only be found on itself
    skip: isCreating || note?.details?.docType !== 'company',
    fetchPolicy: 'cache-and-network'
  })

  const {
    data: dealData,
    loading: isLoadingDeal,
    error: _dealError
  } = useDealQuery({
    variables: { id: note?.details?.deal?.id },
    skip: isCreating || note?.details?.docType !== 'deal',
    fetchPolicy: 'cache-and-network'
  })

  useEffect(() => {
    if (contactData) {
      setNoteDoc(contactData.contact)
    } else if (companyData) {
      setNoteDoc(companyData.company)
    } else if (dealData) {
      setNoteDoc(dealData.deal)
    }
  }, [contactData, companyData, dealData])

  const docFiles = Object.values(noteDoc?.files ?? {}).filter((file: QuoteFile) =>
    note?.details?.files?.includes(file.key)
  ) as QuoteFile[]
  const [initialNote, setInitialNote] = useState<string>(note?.details?.note ?? '')
  const [initialDocs, setInitialDocs] = useState<QuoteFile[]>(
    (docFiles ?? []).filter((f) => note?.details?.files?.includes(f.key))
  )
  const loading =
    fetching || isLoadingCompany || isLoadingContact || isLoadingDeal || (fetching && !note && !!noteId) || isLoading
  const [currentFiles, setCurrentFiles] = useState<QuoteFile[]>(initialDocs)
  const [totalEdits, setTotalEdits] = useState(note?.details?.versions?.length ?? 0)

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, { tags: { noteId } })
      enqueueSnackbar('Unable to load Note', { variant: 'error' })
      onCancel(null)
    }
  }, [error])

  useEffect(() => {
    if (data) {
      setNote(data.getNote)
      setInitialNote(data.getNote?.details?.note ?? '')
      const newInitialDocs = (docFiles ?? []).filter((f) => data.getNote?.details?.files?.includes(f.key))
      setInitialDocs(newInitialDocs)
      setCurrentFiles(newInitialDocs)
      notesRef.current?.setHTML(data.getNote?.details?.note ?? '')
      setTotalEdits(data.getNote?.details?.versions?.length ?? 0)
    }
  }, [data, docFiles.length])

  const handleDelete = (_: React.FormEvent<HTMLElement>) => {
    onDelete(note)
  }

  function handleAddFiles() {
    if (hasCompanyCam && crmFlag) {
      setMenuAnchorEl(null)
      openDialog(EditIntegrationItemFilesDialog, {
        provider: 'companycam',
        maxWidth: 'lg',
        fullWidth: true,
        files: currentFiles,
        onConfirm: (newFiles: QuoteFile[]) => {
          const now = Date.now()
          const existingKeys = new Set(currentFiles.map((f) => f.key))
          const timestampedNewFiles = newFiles
            .filter((f) => !existingKeys.has(f.key))
            .map((f) => ({ ...f, created: f.created ?? now }))
          const mergedFiles = [...currentFiles, ...timestampedNewFiles]
          setCurrentFiles(mergedFiles)
          dismissDialog()
          setIsLoading(false)
        },
        onCancel: () => {
          dismissDialog()
          setIsLoading(false)
        }
      })
    } else {
      setIsLoading(true)
      openDialog(EditItemFilesDialog, {
        fileDocId: docId,
        titleOnly: true,
        files: currentFiles,
        index: currentFiles.length,
        onDelete: (key) => {
          setCurrentFiles((prevFiles) => prevFiles.filter((f) => f.key !== key))
        },
        onConfirm: async (newFiles, _deletedFiles) => {
          setCurrentFiles(newFiles)
          dismissDialog()
          setIsLoading(false)
        },
        onCancel: () => {
          dismissDialog()
          setIsLoading(false)
        }
      })
    }
  }

  function handleUploadButtonClick(e?: React.MouseEvent<HTMLDivElement>) {
    if (hasCompanyCam && crmFlag) {
      setIsLoading(true)
      setMenuAnchorEl(e?.currentTarget ?? null)
    } else if (crmFlag) {
      handleAddFiles()
    }
  }

  function handleDeviceClick() {
    handleMenuClose()
    setIsLoading(true)
    openDialog(EditItemFilesDialog, {
      fileDocId: docId,
      titleOnly: true,
      files: currentFiles,
      index: currentFiles.length,
      onDelete: (key) => {
        setCurrentFiles((prevFiles) => prevFiles.filter((f) => f.key !== key))
      },
      onConfirm: async (newFiles, _deletedFiles) => {
        setCurrentFiles(newFiles)
        dismissDialog()
        setIsLoading(false)
      },
      onCancel: () => {
        dismissDialog()
        setIsLoading(false)
      }
    })
  }

  function handleCompanyCamClick() {
    handleMenuClose()
    setIsLoading(true)
    openDialog(EditIntegrationItemFilesDialog, {
      provider: 'companycam',
      maxWidth: 'lg',
      fullWidth: true,
      files: currentFiles,
      onConfirm: (newFiles: QuoteFile[]) => {
        const now = Date.now()
        const existingKeys = new Set(currentFiles.map((f) => f.key))
        const timestampedNewFiles = newFiles
          .filter((f) => !existingKeys.has(f.key))
          .map((f) => ({ ...f, created: f.created ?? now }))
        const mergedFiles = [...currentFiles, ...timestampedNewFiles]
        setCurrentFiles(mergedFiles)
        dismissDialog()
        setIsLoading(false)
      },
      onCancel: () => {
        dismissDialog()
        setIsLoading(false)
      }
    })
  }

  function handleRemoveFile(file: QuoteFile) {
    setCurrentFiles((prevFiles) => prevFiles.filter((f) => f.key !== file.key))
  }

  function handleEditFile(file: QuoteFile) {
    openDialog(EditItemFilesDialog, {
      fileDocId: docId,
      files: currentFiles,
      index: currentFiles.findIndex((f) => f.key === file.key),
      titleOnly: true,
      onDelete: (key) => {
        setCurrentFiles((prevFiles) => prevFiles.filter((f) => f.key !== key))
      },
      onConfirm: (newFiles, _deletedFiles) => {
        setCurrentFiles(newFiles)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)

  const open = Boolean(popoverAnchorEl)
  function handleClick(e: React.MouseEvent) {
    setPopoverAnchorEl(e.currentTarget)
  }

  function handleClose() {
    setPopoverAnchorEl(null)
  }

  function handleMenuClose() {
    setMenuAnchorEl(null)
  }

  return (
    <Dialog open {...baseDialogProps}>
      <DialogTitle
        rightContent={
          <>
            <div onClick={onCancel} className={classes.closeIcon}>
              <ClearIcon />
            </div>
            <div style={{ position: 'relative' }}>
              {totalEdits > 1 && (
                <div style={{ position: 'absolute', left: '-140px', top: '-18px' }}>
                  <Button variant="text" color="inherit" onClick={handleClick} endIcon={<ArrowDropDownIcon />}>
                    Edits
                  </Button>
                  <Popover
                    elevation={1}
                    open={open}
                    anchorEl={popoverAnchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left'
                    }}
                    transformOrigin={{
                      horizontal: -14,
                      vertical: 0
                    }}
                  >
                    <NoteHistoryPopover note={note} totalEdits={totalEdits} onNoteClick={handleClose} />
                  </Popover>
                </div>
              )}
            </div>
          </>
        }
      >
        <Typography variant="h3">{isCreating ? 'Create New' : 'Edit'} Note</Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <form className={classes.root} autoComplete="off">
          <div className={classes.editorContainer}>
            <Editor
              content={initialNote}
              ref={notesRef}
              fullWidth={true}
              onAddFiles={crmFlag ? handleUploadButtonClick : null}
              onEditFile={handleEditFile}
              disabled={loading}
              files={currentFiles}
              onRemoveFile={handleRemoveFile}
              showFileBar
            />
            {hasCompanyCam && crmFlag && (
              <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
                <MenuItem icon={MediaIcon} onClick={handleDeviceClick}>
                  My Device
                </MenuItem>
                <MenuItem
                  image="https://res.cloudinary.com/taptapquote/image/upload/v1641926710/paintscout/providers/companycam.png"
                  onClick={handleCompanyCamClick}
                >
                  CompanyCam
                </MenuItem>
              </Menu>
            )}
          </div>
        </form>
      </DialogContent>
      <DialogActions
        leftButton={
          isCreating || !deleteEnabled ? null : (
            <Button className={classes.delete} onClick={handleDelete} variant={'text'} disabled={loading}>
              Delete
            </Button>
          )
        }
      >
        <Button
          className={classes.confirm}
          form="note-dialog-form"
          variant={'contained'}
          disabled={isLoading}
          data-testid="note-dialog-confirm-button"
          onClick={() => handleSubmit()}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)))
      const currentNote = notesRef.current.getHTML()
      if (!striptags(currentNote).length) {
        enqueueSnackbar('Note must contain some content.', { variant: 'error' })
        return
      }
      const filesDirty =
        currentFiles.length !== initialDocs.length ||
        currentFiles.some((f) => {
          const initialFile = initialDocs.find((i) => i.key === f.key)
          return !initialFile || initialFile.title !== f.title || initialFile.src !== f.src
        })
      const isDirty = currentNote !== initialNote || filesDirty
      await onConfirm(noteDoc, currentNote, currentFiles, isDirty)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('Error saving note. Please try again.', { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }
}

export default NoteDialog
